import {
  faAngleDown,
  faCalendarAlt,
  faCircle,
  faInfoCircle,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { addDays, differenceInDays, formatISO, subDays, subYears } from 'date-fns';
import React from 'react';
import { Modal } from 'react-bootstrap';
import useCollapse from 'react-collapsed';
import DatePicker from 'react-datepicker';

import { COMPARISON_PERIOD_OPTIONS, INSIGHT_COLORS } from './impactAnalysisTabConstants';
import ImpactAnalysisTabContext from './impactAnalysisTabContext';
import { DashboardContext } from './revenueUpliftContexts';

import LoadingIndicator from '../../../accountSettings/common/LoadingIndicator';
import { ProfitRoverSecondaryButton } from '../../../forms/ProfitRoverButtons';
import { ProfitRoverModalFooter, ProfitRoverModalMain } from '../../../generic/ProfitRoverCard';
import { compactFormatCurrency, formatDeltaPercent, parseLocalDate, shortRange } from '../../../util/format';

import './impact-analysis-tab.scss';

// ============================================
// Constants
// ============================================

const DEFAULT_COMPARISON_PERIOD = 90;
const VERDICT_THRESHOLD = 5;
const FLAT_THRESHOLD = 1;

// ============================================
// Utility Functions
// ============================================

const formatDateStr = date => formatISO(date, { representation: 'date' });

const normalizeDateKey = dateStr => {
  const [year, month, day] = dateStr.split('-');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const safeDeltaPct = (post, pre) => (pre !== 0 ? ((post - pre) / pre) * 100 : 0);

// ============================================
// Data Extraction & Period Computation
// ============================================

const extractDailyMetrics = rows => {
  const dateRegex = /^(revenue|demand)_(\d{4}-\d{1,2}-\d{1,2})$/;
  const metrics = {};

  rows.forEach(row => {
    Object.entries(row).forEach(([key, value]) => {
      const match = key.match(dateRegex);
      if (match && typeof value === 'number') {
        const [, type, rawDate] = match;
        const date = normalizeDateKey(rawDate);
        if (!metrics[date]) {
          metrics[date] = { revenue: 0, demand: 0 };
        }
        metrics[date][type] += value;
      }
    });
  });

  return metrics;
};

const computePeriodMetrics = (dailyMetrics, startDate, endDate) => {
  let totalRevenue = 0;
  let totalDemand = 0;
  let daysWithData = 0;

  const current = new Date(startDate);
  while (current <= endDate) {
    const dateStr = formatDateStr(current);
    if (dailyMetrics[dateStr]) {
      totalRevenue += dailyMetrics[dateStr].revenue || 0;
      totalDemand += dailyMetrics[dateStr].demand || 0;
      daysWithData += 1;
    }
    current.setDate(current.getDate() + 1);
  }

  const days = differenceInDays(endDate, startDate) + 1;
  const avgDailyRevenue = daysWithData > 0 ? totalRevenue / daysWithData : 0;
  const avgDailyQty = daysWithData > 0 ? totalDemand / daysWithData : 0;

  return { totalRevenue, totalDemand, avgDailyRevenue, avgDailyQty, days, daysWithData };
};

const buildMetricsResult = (preMetrics, postMetrics) => {
  const revenueDelta = postMetrics.avgDailyRevenue - preMetrics.avgDailyRevenue;
  const revenueDeltaPct = safeDeltaPct(postMetrics.avgDailyRevenue, preMetrics.avgDailyRevenue);
  const qtyDelta = postMetrics.avgDailyQty - preMetrics.avgDailyQty;
  const qtyDeltaPct = safeDeltaPct(postMetrics.avgDailyQty, preMetrics.avgDailyQty);

  return {
    pre_avg_daily_revenue: preMetrics.avgDailyRevenue,
    post_avg_daily_revenue: postMetrics.avgDailyRevenue,
    revenue_delta: revenueDelta,
    revenue_delta_pct: revenueDeltaPct,
    pre_avg_daily_qty: preMetrics.avgDailyQty,
    post_avg_daily_qty: postMetrics.avgDailyQty,
    qty_delta: qtyDelta,
    qty_delta_pct: qtyDeltaPct,
  };
};

// ============================================
// Verdict Generators
// ============================================

const getFilteredProductVerdict = (filteredMetrics, currencySymbol) => {
  const pct = filteredMetrics.revenue_delta_pct;
  const absDelta = compactFormatCurrency(Math.abs(filteredMetrics.revenue_delta), currencySymbol);

  if (Math.abs(pct) < FLAT_THRESHOLD) {
    return {
      color: INSIGHT_COLORS.MIXED_RESULTS,
      sentence: 'Average daily revenue remained essentially flat after the change.',
    };
  }

  if (pct > 0) {
    return {
      color: INSIGHT_COLORS.NET_POSITIVE_IMPACT,
      sentence: `Average daily revenue increased by ${absDelta} (${formatDeltaPercent(pct)}) after the change.`,
    };
  }

  return {
    color: INSIGHT_COLORS.NET_NEGATIVE_IMPACT,
    sentence: `Average daily revenue decreased by ${absDelta} (${formatDeltaPercent(pct)}) after the change.`,
  };
};

const getSeasonalVerdict = stlyMetrics => {
  const improvementVsSTLY = stlyMetrics.post_vs_stly_pct - stlyMetrics.pre_vs_stly_pct;

  if (improvementVsSTLY > VERDICT_THRESHOLD) {
    return {
      color: INSIGHT_COLORS.NET_POSITIVE_IMPACT,
      sentence:
        `Post-change performance outpaces last year's trend by ` +
        `${formatDeltaPercent(improvementVsSTLY)}, suggesting real improvement beyond seasonal patterns.`,
    };
  }

  if (improvementVsSTLY < -VERDICT_THRESHOLD) {
    return {
      color: INSIGHT_COLORS.NET_NEGATIVE_IMPACT,
      sentence:
        `Post-change performance trails last year's seasonal trend by ` +
        `${formatDeltaPercent(improvementVsSTLY)}, suggesting underperformance.`,
    };
  }

  return {
    color: INSIGHT_COLORS.LIKELY_SEASONAL,
    sentence: "Performance roughly matches last year's seasonal pattern — changes align with expected trends.",
  };
};

const getOverallImpactVerdict = (filteredMetrics, overallMetrics) => {
  const filteredUp = filteredMetrics.revenue_delta_pct > FLAT_THRESHOLD;
  const filteredDown = filteredMetrics.revenue_delta_pct < -FLAT_THRESHOLD;
  const overallUp = overallMetrics.revenue_delta_pct > FLAT_THRESHOLD;
  const overallDown = overallMetrics.revenue_delta_pct < -FLAT_THRESHOLD;

  if (filteredUp && overallUp) {
    return {
      color: INSIGHT_COLORS.NET_POSITIVE_IMPACT,
      sentence: 'Both the selected products and the overall business improved, supporting genuine positive impact.',
    };
  }

  if (filteredUp && overallDown) {
    return {
      color: INSIGHT_COLORS.REVENUE_SHIFTED,
      sentence:
        'Selected products improved but overall business declined — revenue may have shifted from other products.',
    };
  }

  if (filteredDown && overallDown) {
    return {
      color: INSIGHT_COLORS.NET_NEGATIVE_IMPACT,
      sentence: 'Both the selected products and the overall business declined after the change.',
    };
  }

  if (filteredDown && overallUp) {
    return {
      color: INSIGHT_COLORS.REVENUE_SHIFTED,
      sentence: 'Selected products declined but overall business improved — growth is coming from other products.',
    };
  }

  return {
    color: INSIGHT_COLORS.MIXED_RESULTS,
    sentence: 'Results are mixed across filtered and overall business performance.',
  };
};

// ============================================
// Main Computation
// ============================================

const computeImpactAnalysis = (
  filteredDailyPerformanceHistory,
  unfilteredDailyPerformanceHistory,
  changeDate,
  comparisonPeriod,
  hasActiveFilters,
  currencySymbol,
) => {
  if (!filteredDailyPerformanceHistory || filteredDailyPerformanceHistory.length === 0 || !changeDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const changeDateObj = new Date(changeDate);
  changeDateObj.setHours(0, 0, 0, 0);

  const daysSinceChange = differenceInDays(today, changeDateObj);
  if (daysSinceChange <= 0) {
    return null;
  }

  const effectiveDays = Math.min(daysSinceChange, comparisonPeriod);

  // Define periods
  const prePeriodStart = subDays(changeDateObj, effectiveDays);
  const prePeriodEnd = subDays(changeDateObj, 1);
  const postPeriodStart = changeDateObj;
  const postPeriodEnd = effectiveDays < daysSinceChange ? addDays(changeDateObj, effectiveDays - 1) : today;

  // STLY periods
  const stlyPreStart = subYears(prePeriodStart, 1);
  const stlyPreEnd = subYears(prePeriodEnd, 1);
  const stlyPostStart = subYears(postPeriodStart, 1);
  const stlyPostEnd = subYears(postPeriodEnd, 1);

  // Filtered metrics (Section 1)
  const filteredDailyMetrics = extractDailyMetrics(filteredDailyPerformanceHistory);
  const filteredPreMetrics = computePeriodMetrics(filteredDailyMetrics, prePeriodStart, prePeriodEnd);
  const filteredPostMetrics = computePeriodMetrics(filteredDailyMetrics, postPeriodStart, postPeriodEnd);
  const filteredResult = buildMetricsResult(filteredPreMetrics, filteredPostMetrics);

  // STLY metrics (Section 2)
  const stlyPreMetrics = computePeriodMetrics(filteredDailyMetrics, stlyPreStart, stlyPreEnd);
  const stlyPostMetrics = computePeriodMetrics(filteredDailyMetrics, stlyPostStart, stlyPostEnd);
  const stlyAvailable = stlyPreMetrics.daysWithData >= 7 && stlyPostMetrics.daysWithData >= 7;

  const seasonalUpliftPct = safeDeltaPct(stlyPostMetrics.avgDailyRevenue, stlyPreMetrics.avgDailyRevenue);

  const preVsStly =
    stlyPreMetrics.avgDailyRevenue !== 0
      ? (filteredPreMetrics.avgDailyRevenue / stlyPreMetrics.avgDailyRevenue - 1) * 100
      : 0;
  const postVsStly =
    stlyPostMetrics.avgDailyRevenue !== 0
      ? (filteredPostMetrics.avgDailyRevenue / stlyPostMetrics.avgDailyRevenue - 1) * 100
      : 0;

  const stlyResult = {
    pre_vs_stly_pct: preVsStly,
    post_vs_stly_pct: postVsStly,
    seasonal_uplift_pct: seasonalUpliftPct,
    stly_pre_avg_daily_revenue: stlyPreMetrics.avgDailyRevenue,
    stly_post_avg_daily_revenue: stlyPostMetrics.avgDailyRevenue,
    stly_revenue_delta_pct: seasonalUpliftPct,
    stly_pre_avg_daily_qty: stlyPreMetrics.avgDailyQty,
    stly_post_avg_daily_qty: stlyPostMetrics.avgDailyQty,
    stly_qty_delta: stlyPostMetrics.avgDailyQty - stlyPreMetrics.avgDailyQty,
    stly_qty_delta_pct: safeDeltaPct(stlyPostMetrics.avgDailyQty, stlyPreMetrics.avgDailyQty),
  };

  // Overall metrics (Section 3) — only when filters are active
  let overallResult = null;
  if (hasActiveFilters && unfilteredDailyPerformanceHistory && unfilteredDailyPerformanceHistory.length > 0) {
    const overallDailyMetrics = extractDailyMetrics(unfilteredDailyPerformanceHistory);
    const overallPreMetrics = computePeriodMetrics(overallDailyMetrics, prePeriodStart, prePeriodEnd);
    const overallPostMetrics = computePeriodMetrics(overallDailyMetrics, postPeriodStart, postPeriodEnd);
    overallResult = buildMetricsResult(overallPreMetrics, overallPostMetrics);
  }

  // Verdicts
  const section1Verdict = getFilteredProductVerdict(filteredResult, currencySymbol);
  const section2Verdict = stlyAvailable ? getSeasonalVerdict(stlyResult) : null;
  const section3Verdict = overallResult ? getOverallImpactVerdict(filteredResult, overallResult) : null;

  return {
    filtered_metrics: filteredResult,
    overall_metrics: overallResult,
    stly_metrics: stlyResult,
    stly_available: stlyAvailable,
    has_active_filters: hasActiveFilters,
    verdicts: {
      section1: section1Verdict,
      section2: section2Verdict,
      section3: section3Verdict,
    },
    periods: {
      pre: { start: formatDateStr(prePeriodStart), end: formatDateStr(prePeriodEnd), days: effectiveDays },
      post: { start: formatDateStr(postPeriodStart), end: formatDateStr(postPeriodEnd), days: effectiveDays },
    },
    stly_periods: {
      pre: { start: formatDateStr(stlyPreStart), end: formatDateStr(stlyPreEnd) },
      post: { start: formatDateStr(stlyPostStart), end: formatDateStr(stlyPostEnd) },
    },
  };
};

// ============================================
// Presentational Components
// ============================================

const InfoModal = ({ show, onHide }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Header>
      <Modal.Title>About Impact Analysis</Modal.Title>
    </Modal.Header>
    <ProfitRoverModalMain className="info-modal-body">
      <h6>What is Impact Analysis?</h6>
      <p>
        This tool helps you understand the true impact of a business change &mdash; such as a pricing update, a new
        promotion, or adjusted operating hours &mdash; by comparing your average daily performance before and after the
        change.
      </p>
      <h6>How the Comparison Works</h6>
      <p>
        Select the date your change went into effect and a comparison period. Equal-length windows before and after that
        date are used to compare average daily revenue and sales quantity.
      </p>
      <h6>Understanding Seasonality (STLY)</h6>
      <p>
        To distinguish genuine impact from seasonal trends, your current performance is compared against the Same Time
        Last Year. If both the before and after periods show similar year-over-year patterns, the observed change may
        reflect seasonality rather than a true business impact.
      </p>
      <p className="info-modal-note">
        <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
        Analysis uses your current dashboard filters. Use the Filters menu to analyze specific products or locations.
      </p>
    </ProfitRoverModalMain>
    <ProfitRoverModalFooter className="info-modal-footer">
      <ProfitRoverSecondaryButton onClick={onHide}>Close</ProfitRoverSecondaryButton>
    </ProfitRoverModalFooter>
  </Modal>
);

const TrafficLight = ({ color }) => (
  <FontAwesomeIcon icon={faCircle} style={{ color, fontSize: '0.75rem' }} className="traffic-light-dot" />
);

const ScorecardSection = ({ question, verdict, children }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded, duration: 333 });

  return (
    <div className="scorecard-section">
      <div className="scorecard-header" {...getToggleProps({ onClick: () => setIsExpanded(prev => !prev) })}>
        <TrafficLight color={verdict.color} />
        <div className="scorecard-text">
          <div className="scorecard-question">{question}</div>
          <div className="scorecard-verdict">{verdict.sentence}</div>
        </div>
        <FontAwesomeIcon icon={faAngleDown} className={classnames('scorecard-chevron', { expanded: isExpanded })} />
      </div>
      <div {...getCollapseProps()}>
        <div className="scorecard-body">{children}</div>
      </div>
    </div>
  );
};

const HorizontalBarChart = ({
  title,
  preValue,
  postValue,
  delta,
  deltaPct,
  preStart,
  preEnd,
  postStart,
  postEnd,
  currencySymbol,
  isQuantity,
}) => {
  const formatValue = val =>
    isQuantity ? Math.round(val).toLocaleString() : compactFormatCurrency(val, currencySymbol);

  const maxValue = Math.max(Math.abs(preValue), Math.abs(postValue));
  const preWidth = maxValue > 0 ? (Math.abs(preValue) / maxValue) * 100 : 0;
  const postWidth = maxValue > 0 ? (Math.abs(postValue) / maxValue) * 100 : 0;

  const deltaColor = delta >= 0 ? '#28a745' : '#dc3545';
  const deltaSign = delta >= 0 ? '+' : '-';
  const formattedAbsDelta = isQuantity
    ? Math.abs(Math.round(delta)).toLocaleString()
    : compactFormatCurrency(Math.abs(delta), currencySymbol);

  const preDateRange = shortRange(parseLocalDate(preStart), parseLocalDate(preEnd));
  const postDateRange = shortRange(parseLocalDate(postStart), parseLocalDate(postEnd));

  return (
    <div className="bar-chart-section">
      <h6 className="bar-chart-title">{title}</h6>
      <div className="bar-chart-content">
        <div className="bars-area">
          <div className="bar-group">
            <div className="bar-title">Before Change</div>
            <div className="bar-dates">{preDateRange}</div>
            <div className="bar-track">
              <div className="bar-bg">
                <div className="bar-fill before" style={{ width: `${preWidth}%` }} />
              </div>
              <span className="bar-amount">{formatValue(preValue)}</span>
            </div>
          </div>
          <div className="bar-group">
            <div className="bar-title">After Change</div>
            <div className="bar-dates">{postDateRange}</div>
            <div className="bar-track">
              <div className="bar-bg">
                <div className="bar-fill after" style={{ width: `${postWidth}%` }} />
              </div>
              <span className="bar-amount">{formatValue(postValue)}</span>
            </div>
          </div>
        </div>
        <div className="change-indicator" style={{ color: deltaColor }}>
          <span className="change-amount">
            {deltaSign}
            {formattedAbsDelta}
          </span>
          <span className="change-pct">({formatDeltaPercent(deltaPct)})</span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Main Tab Component
// ============================================

const ImpactAnalysisTab = () => {
  const { currencySymbol } = React.useContext(DashboardContext);
  const { analysisResult, isLoading } = React.useContext(ImpactAnalysisTabContext);

  if (isLoading) {
    return (
      <div className="impact-analysis-loading">
        <LoadingIndicator />
        <p>Loading performance data...</p>
      </div>
    );
  }

  if (!analysisResult) {
    return (
      <div className="impact-analysis-empty">
        <p>Select a change date to compute the impact analysis.</p>
      </div>
    );
  }

  const {
    filtered_metrics: filteredMetrics,
    overall_metrics: overallMetrics,
    stly_metrics: stlyMetrics,
    stly_available: stlyAvailable,
    has_active_filters: hasActiveFilters,
    verdicts,
    periods,
    stly_periods: stlyPeriods,
  } = analysisResult;

  return (
    <div className="impact-analysis-results">
      {/* Section 1: Filtered Product Impact — always visible */}
      <ScorecardSection question="Did performance change after the business change?" verdict={verdicts.section1}>
        <div className="bar-charts-grid">
          <HorizontalBarChart
            title="Avg Daily Revenue"
            preValue={filteredMetrics.pre_avg_daily_revenue}
            postValue={filteredMetrics.post_avg_daily_revenue}
            delta={filteredMetrics.revenue_delta}
            deltaPct={filteredMetrics.revenue_delta_pct}
            preStart={periods.pre.start}
            preEnd={periods.pre.end}
            postStart={periods.post.start}
            postEnd={periods.post.end}
            currencySymbol={currencySymbol}
            isQuantity={false}
          />
          <HorizontalBarChart
            title="Avg Daily Sales Qty"
            preValue={filteredMetrics.pre_avg_daily_qty}
            postValue={filteredMetrics.post_avg_daily_qty}
            delta={filteredMetrics.qty_delta}
            deltaPct={filteredMetrics.qty_delta_pct}
            preStart={periods.pre.start}
            preEnd={periods.pre.end}
            postStart={periods.post.start}
            postEnd={periods.post.end}
            currencySymbol={currencySymbol}
            isQuantity
          />
        </div>
      </ScorecardSection>

      {/* Section 2: Seasonal Context — only if STLY data exists */}
      {stlyAvailable && (
        <ScorecardSection question="Does this follow expected seasonal patterns?" verdict={verdicts.section2}>
          <div className="bar-charts-grid">
            <HorizontalBarChart
              title="This Year: Avg Daily Revenue"
              preValue={filteredMetrics.pre_avg_daily_revenue}
              postValue={filteredMetrics.post_avg_daily_revenue}
              delta={filteredMetrics.revenue_delta}
              deltaPct={filteredMetrics.revenue_delta_pct}
              preStart={periods.pre.start}
              preEnd={periods.pre.end}
              postStart={periods.post.start}
              postEnd={periods.post.end}
              currencySymbol={currencySymbol}
              isQuantity={false}
            />
            <HorizontalBarChart
              title="Last Year (STLY): Avg Daily Revenue"
              preValue={stlyMetrics.stly_pre_avg_daily_revenue}
              postValue={stlyMetrics.stly_post_avg_daily_revenue}
              delta={stlyMetrics.stly_post_avg_daily_revenue - stlyMetrics.stly_pre_avg_daily_revenue}
              deltaPct={stlyMetrics.stly_revenue_delta_pct}
              preStart={stlyPeriods.pre.start}
              preEnd={stlyPeriods.pre.end}
              postStart={stlyPeriods.post.start}
              postEnd={stlyPeriods.post.end}
              currencySymbol={currencySymbol}
              isQuantity={false}
            />
          </div>
          <div className="stly-summary">
            <div className="stly-summary-item">
              <span className="stly-summary-label">Pre-Period vs STLY:</span>
              <span className="stly-summary-value">{formatDeltaPercent(stlyMetrics.pre_vs_stly_pct)}</span>
            </div>
            <div className="stly-summary-item">
              <span className="stly-summary-label">Post-Period vs STLY:</span>
              <span className="stly-summary-value">{formatDeltaPercent(stlyMetrics.post_vs_stly_pct)}</span>
            </div>
            <div className="stly-summary-item">
              <span className="stly-summary-label">Seasonal Uplift (STLY):</span>
              <span className="stly-summary-value">{formatDeltaPercent(stlyMetrics.seasonal_uplift_pct)}</span>
            </div>
          </div>
        </ScorecardSection>
      )}

      {/* Section 2 fallback — STLY unavailable */}
      {!stlyAvailable && (
        <div className="scorecard-section scorecard-unavailable">
          <div className="scorecard-header scorecard-header--disabled">
            <FontAwesomeIcon icon={faInfoCircle} style={{ color: INSIGHT_COLORS.MIXED_RESULTS, fontSize: '0.75rem' }} />
            <div className="scorecard-text">
              <div className="scorecard-question">Seasonal context unavailable</div>
              <div className="scorecard-verdict">
                Less than 1 year of data &mdash; cannot compare to same time last year.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section 3: Overall Business Impact — only if filters applied */}
      {hasActiveFilters && overallMetrics && verdicts.section3 && (
        <ScorecardSection question="What did this change do to the overall business?" verdict={verdicts.section3}>
          <div className="bar-charts-grid">
            <HorizontalBarChart
              title="Overall Avg Daily Revenue"
              preValue={overallMetrics.pre_avg_daily_revenue}
              postValue={overallMetrics.post_avg_daily_revenue}
              delta={overallMetrics.revenue_delta}
              deltaPct={overallMetrics.revenue_delta_pct}
              preStart={periods.pre.start}
              preEnd={periods.pre.end}
              postStart={periods.post.start}
              postEnd={periods.post.end}
              currencySymbol={currencySymbol}
              isQuantity={false}
            />
            <HorizontalBarChart
              title="Overall Avg Daily Sales Qty"
              preValue={overallMetrics.pre_avg_daily_qty}
              postValue={overallMetrics.post_avg_daily_qty}
              delta={overallMetrics.qty_delta}
              deltaPct={overallMetrics.qty_delta_pct}
              preStart={periods.pre.start}
              preEnd={periods.pre.end}
              postStart={periods.post.start}
              postEnd={periods.post.end}
              currencySymbol={currencySymbol}
              isQuantity
            />
          </div>
        </ScorecardSection>
      )}
    </div>
  );
};

// ============================================
// Context Controller
// ============================================

const ImpactAnalysisTabContextController = ({ children }) => {
  const {
    dailyPerformanceHistory,
    unfilteredDailyPerformanceHistory,
    isLoadingPerformance,
    filteredDimensionsSet,
    currencySymbol,
  } = React.useContext(DashboardContext);

  const [changeDate, setChangeDate] = React.useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });

  const [comparisonPeriod, setComparisonPeriod] = React.useState(DEFAULT_COMPARISON_PERIOD);

  const hasActiveFilters = filteredDimensionsSet && filteredDimensionsSet.size > 0;

  const analysisResult = React.useMemo(
    () =>
      computeImpactAnalysis(
        dailyPerformanceHistory,
        unfilteredDailyPerformanceHistory,
        changeDate,
        comparisonPeriod,
        hasActiveFilters,
        currencySymbol,
      ),
    [
      dailyPerformanceHistory,
      unfilteredDailyPerformanceHistory,
      changeDate,
      comparisonPeriod,
      hasActiveFilters,
      currencySymbol,
    ],
  );

  const contextValue = {
    changeDate,
    setChangeDate,
    comparisonPeriod,
    setComparisonPeriod,
    analysisResult,
    isLoading: isLoadingPerformance,
  };

  return <ImpactAnalysisTabContext.Provider value={contextValue}>{children}</ImpactAnalysisTabContext.Provider>;
};

// ============================================
// Config Bar, Header, Container
// ============================================

const ConfigBar = () => {
  const { changeDate, setChangeDate, comparisonPeriod, setComparisonPeriod } = React.useContext(
    ImpactAnalysisTabContext,
  );

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() - 1);

  return (
    <div className="impact-config-bar">
      <div className="config-item">
        <label htmlFor="impact-change-date">When did the change happen?</label>
        <div className="date-picker-wrapper">
          <DatePicker
            id="impact-change-date"
            selected={changeDate}
            onChange={setChangeDate}
            maxDate={maxDate}
            dateFormat="MMM d, yyyy"
            className="form-control"
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
        </div>
      </div>
      <div className="config-item">
        <label htmlFor="impact-comparison-period">Comparison Period</label>
        <select
          id="impact-comparison-period"
          className="form-control"
          value={comparisonPeriod}
          onChange={e => setComparisonPeriod(Number(e.target.value))}
        >
          {COMPARISON_PERIOD_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const Header = () => {
  const [showInfo, setShowInfo] = React.useState(false);

  return (
    <div className="impact-header">
      <div className="impact-title-row">
        <h5>Analyze the Impact of a Business Change</h5>
        <button
          type="button"
          className="info-icon-btn"
          onClick={() => setShowInfo(true)}
          aria-label="Learn more about Impact Analysis"
        >
          <FontAwesomeIcon icon={faQuestionCircle} />
        </button>
      </div>
      <InfoModal show={showInfo} onHide={() => setShowInfo(false)} />
    </div>
  );
};

const ImpactAnalysisContent = () => (
  <div className="impact-analysis-tab">
    <Header />
    <ConfigBar />
    <ImpactAnalysisTab />
  </div>
);

const ImpactAnalysisTabContainer = ({ isActive }) => (
  <ImpactAnalysisTabContextController isActive={isActive}>
    <ImpactAnalysisContent />
  </ImpactAnalysisTabContextController>
);

export default ImpactAnalysisTabContainer;
